import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Users, Shield, ChevronRight, Syringe, Utensils, Scale, Pill, X, Pin, ArrowLeft, Info, UserCog, Eye, Repeat, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import CoachNotesPanel from "./CoachNotesPanel";
import ClientActivityFeed from "./ClientActivityFeed";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";
const AMBER = "#F59E0B";

/** Super-admin support view mounted on /clients.
 *  Unified global search over coaches + clients, coach impersonation, and a
 *  client drawer with reassignment + admin-note posting.
 */
export default function AdminGlobalCoachingView() {
  const [data, setData] = useState({ coaches: [], clients: [], counts: null });
  const [loading, setLoading] = useState(true);
  const [globalQ, setGlobalQ] = useState("");
  const [coachFilter, setCoachFilter] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [impersonate, setImpersonate] = useState(null); // {coach, clients} or null

  const load = () => {
    setLoading(true);
    api.get("/coach/admin-overview")
      .then(({ data }) => setData({
        coaches: data.coaches || [],
        clients: data.clients || [],
        counts: data.counts || null,
      }))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const q = globalQ.trim().toLowerCase();
  const filteredCoaches = useMemo(() => {
    if (!q) return data.coaches;
    return data.coaches.filter((c) =>
      (c.email || "").toLowerCase().includes(q) ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.handle || "").toLowerCase().includes(q)
    );
  }, [q, data.coaches]);

  const filteredClients = useMemo(() => {
    let list = data.clients;
    if (coachFilter) list = list.filter((c) => c.coach_user_id === coachFilter);
    if (!q) return list;
    return list.filter((c) =>
      (c.email || "").toLowerCase().includes(q) ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.handle || "").toLowerCase().includes(q) ||
      (c.coach_name || "").toLowerCase().includes(q) ||
      (c.coach_email || "").toLowerCase().includes(q)
    );
  }, [q, coachFilter, data.clients]);

  const openClient = async (c) => {
    setSelectedClient(c);
    setOverview(null);
    setOvLoading(true);
    try {
      const { data } = await api.get(`/coach/clients/${c.user_id}/overview`);
      setOverview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOvLoading(false);
    }
  };

  const startImpersonate = async (coach) => {
    try {
      const { data } = await api.get(`/coach/admin-impersonate/${coach.user_id}`);
      setImpersonate({ coach: data.coach, clients: data.clients || [] });
      setCoachFilter(null);
      toast.success(`Viewing as ${data.coach.name || "@" + data.coach.handle}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onReassigned = (shareId, newCoachEmail, newCoachName) => {
    // Update in-memory list so the drawer reflects the new assignment
    setData((prev) => ({
      ...prev,
      clients: prev.clients.map((c) => c.share_id === shareId
        ? { ...c, coach_email: newCoachEmail, coach_name: newCoachName, coach_user_id: data.coaches.find(k => (k.email || "").toLowerCase() === newCoachEmail.toLowerCase())?.user_id || c.coach_user_id }
        : c),
    }));
    if (selectedClient?.share_id === shareId) {
      setSelectedClient((prev) => prev ? { ...prev, coach_email: newCoachEmail, coach_name: newCoachName } : prev);
    }
  };

  if (impersonate) {
    return (
      <ImpersonateView
        coach={impersonate.coach}
        clients={impersonate.clients}
        onExit={() => setImpersonate(null)}
        onOpenClient={(c) => openClient({ ...c, coach_email: impersonate.coach.email, coach_name: impersonate.coach.name })}
        selectedClient={selectedClient}
        overview={overview}
        ovLoading={ovLoading}
        onCloseDrawer={() => { setSelectedClient(null); setOverview(null); }}
        allCoaches={data.coaches}
        onReassigned={onReassigned}
      />
    );
  }

  return (
    <div className="mt-6" data-testid="admin-coaching-view">
      {/* Support banner */}
      <div className="rounded-xl border-l-4 p-4 mb-4 flex items-start gap-3"
           style={{ borderLeftColor: AMBER, background: "rgba(251, 191, 36, 0.06)" }}
           data-testid="admin-coaching-banner">
        <Shield className="w-5 h-5 mt-0.5" style={{ color: AMBER }} />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: AMBER }}>
            Admin support view
          </div>
          <div className="text-sm text-slate-700 mt-0.5">
            Global view of every coach + every client. Search either side, impersonate a specific coach, reassign a client, or drop a shared admin note in a client's thread.
          </div>
          {data.counts && (
            <div className="text-[11px] text-slate-500 mt-2 flex flex-wrap gap-3 font-mono-num">
              <span><b className="text-slate-800">{data.counts.coaches}</b> coaches</span>
              <span className="text-slate-300">·</span>
              <span><b className="text-slate-800">{data.counts.clients_with_coach}</b> clients with a coach</span>
              <span className="text-slate-300">·</span>
              <span><b className="text-slate-800">{data.counts.total_users}</b> total users</span>
            </div>
          )}
        </div>
      </div>

      {/* Global search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={globalQ}
          onChange={(e) => setGlobalQ(e.target.value)}
          placeholder="Search coaches or clients — name, handle, email, or assigned coach…"
          className="pl-10 h-11 bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
          data-testid="admin-coaching-global-search"
        />
        {globalQ && (
          <button
            type="button"
            onClick={() => setGlobalQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="admin-coaching-global-search-clear"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* All Coaches */}
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-coaching-coaches-panel">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4" style={{ color: TEAL }} />
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>All coaches</div>
              <span className="text-[10px] text-slate-400 tabular-nums ml-1">{filteredCoaches.length}</span>
              {coachFilter && (
                <button
                  type="button"
                  onClick={() => setCoachFilter(null)}
                  className="ml-auto text-[11px] text-teal-700 hover:text-teal-900 inline-flex items-center gap-1"
                  data-testid="admin-coaching-clear-coach-filter"
                >
                  <X className="w-3 h-3" /> Clear filter
                </button>
              )}
            </div>
            {filteredCoaches.length === 0 ? (
              <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
                {data.coaches.length === 0 ? "No coaches on the platform yet." : "No coaches match this search."}
              </div>
            ) : (
              <ul className="space-y-1.5 max-h-[420px] overflow-auto">
                {filteredCoaches.map((c) => {
                  const active = coachFilter === c.user_id;
                  return (
                    <li key={c.user_id}
                        className={`p-2.5 rounded-md flex items-center gap-3 border ${
                          active
                            ? "border-teal-300 bg-teal-50"
                            : c.is_featured
                              ? "border-amber-200 bg-amber-50/30"
                              : "border-slate-100 hover:bg-slate-50 hover:border-slate-200"
                        }`}
                        data-testid={`admin-coaching-coach-row-${c.user_id}`}>
                      <button
                        type="button"
                        onClick={() => setCoachFilter(active ? null : c.user_id)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                             style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}>
                          {(c.handle || c.name || c.email || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {c.name || `@${c.handle}`}
                            </div>
                            {c.is_featured && (
                              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700">
                                <Pin className="w-2.5 h-2.5" /> Featured
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono-num truncate">{c.email}</div>
                        </div>
                        <div className="text-[11px] text-slate-500 shrink-0 text-right pr-2">
                          <div><b className="text-slate-800 tabular-nums">{c.active_client_count}</b></div>
                          <div className="text-[9px] uppercase tracking-widest text-slate-400">clients</div>
                        </div>
                      </button>
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => startImpersonate(c)}
                        className="text-teal-700 hover:bg-teal-50 shrink-0 h-7 px-2"
                        data-testid={`admin-coaching-impersonate-${c.user_id}`}
                        title={`View as ${c.name || "@" + c.handle}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        <span className="text-[10px] uppercase tracking-widest">View as</span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="text-[10px] text-slate-400 mt-3 flex items-center gap-1.5">
              <Info className="w-3 h-3" /> Tap a coach row to filter clients · tap "View as" to fully impersonate.
            </div>
          </div>

          {/* All Clients */}
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-coaching-clients-panel">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4" style={{ color: TEAL }} />
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>All clients (with a coach)</div>
              <span className="text-[10px] text-slate-400 tabular-nums ml-1">{filteredClients.length}</span>
            </div>
            {filteredClients.length === 0 ? (
              <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
                {data.clients.length === 0 ? "No clients have accepted a coach yet." : "No clients match this filter."}
              </div>
            ) : (
              <ul className="space-y-1.5 max-h-[420px] overflow-auto">
                {filteredClients.map((c) => (
                  <li key={c.user_id}>
                    <button
                      type="button"
                      onClick={() => openClient(c)}
                      className="w-full text-left p-2.5 rounded-md flex items-center gap-3 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-colors"
                      data-testid={`admin-coaching-client-row-${c.user_id}`}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                           style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}>
                        {(c.handle || c.name || c.email || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || `@${c.handle}`}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          <span className="text-slate-400">Assigned to</span> <b className="text-teal-700">{c.coach_name || c.coach_email}</b>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {selectedClient && (
        <AdminClientDrawer
          client={selectedClient}
          overview={overview}
          loading={ovLoading}
          allCoaches={data.coaches}
          onClose={() => { setSelectedClient(null); setOverview(null); }}
          onReassigned={onReassigned}
        />
      )}
    </div>
  );
}

function ImpersonateView({ coach, clients, onExit, onOpenClient, selectedClient, overview, ovLoading, onCloseDrawer, allCoaches, onReassigned }) {
  return (
    <div className="mt-6" data-testid="admin-impersonate-view">
      <div className="rounded-xl border-l-4 p-4 mb-4 flex items-center gap-3"
           style={{ borderLeftColor: TEAL, background: "rgba(13, 148, 136, 0.06)" }}
           data-testid="admin-impersonate-banner">
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs text-white shrink-0"
             style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}>
          {(coach.handle || coach.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>
            <Shield className="w-3 h-3 inline mr-1" /> Impersonating coach — read-only
          </div>
          <div className="font-display font-bold text-lg text-slate-900">
            {coach.name || `@${coach.handle}`} <span className="text-slate-400 font-normal text-sm">· {coach.email}</span>
          </div>
        </div>
        <Button
          type="button" size="sm" variant="outline"
          onClick={onExit}
          className="border-slate-300 text-slate-700 hover:bg-slate-100"
          data-testid="admin-impersonate-exit"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Exit view-as
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4" style={{ color: TEAL }} />
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Their clients</div>
          <span className="text-[10px] text-slate-400 tabular-nums ml-1">{clients.length}</span>
        </div>
        {clients.length === 0 ? (
          <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-6 text-center">
            This coach has no active clients yet.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {clients.map((c) => (
              <li key={c.user_id}>
                <button
                  type="button"
                  onClick={() => onOpenClient(c)}
                  className="w-full text-left p-2.5 rounded-md flex items-center gap-3 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-colors"
                  data-testid={`admin-impersonate-client-row-${c.user_id}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                       style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}>
                    {(c.handle || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.name || `@${c.handle}`}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedClient && (
        <AdminClientDrawer
          client={selectedClient}
          overview={overview}
          loading={ovLoading}
          allCoaches={allCoaches}
          onClose={onCloseDrawer}
          onReassigned={onReassigned}
        />
      )}
    </div>
  );
}

function AdminClientDrawer({ client, overview, loading, allCoaches, onClose, onReassigned }) {
  const [reassignOpen, setReassignOpen] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[9985] bg-slate-900/50 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      data-testid="admin-client-drawer-overlay"
    >
      <div
        className="bg-slate-50 w-full max-w-3xl h-full overflow-y-auto shadow-2xl border-l border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-client-drawer"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3">
          <button type="button" onClick={onClose}
                  className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm"
                  data-testid="admin-client-drawer-close">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="ml-2 flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: AMBER }}>Admin support view</div>
            <div className="font-display font-bold text-lg text-slate-900 truncate">
              {client.name || `@${client.handle}`} <span className="text-slate-400 font-normal text-sm">· {client.email}</span>
            </div>
            <div className="text-[11px] text-slate-500">
              Assigned coach: <b className="text-teal-700">{client.coach_name || client.coach_email}</b>
            </div>
          </div>
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => setReassignOpen(true)}
            disabled={!client.share_id}
            className="border-amber-200 text-amber-700 hover:bg-amber-50 shrink-0"
            data-testid="admin-client-reassign-open"
            title="Reassign this client to another coach"
          >
            <Repeat className="w-3.5 h-3.5 mr-1" /> Reassign
          </Button>
        </div>

        <div className="p-5 space-y-4">
          {loading && <div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" />}
          {!loading && overview && (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <Stat icon={Scale} label="Body weight" value={overview.client.body_weight ? `${overview.client.body_weight} ${overview.client.weight_unit || "lb"}` : "—"} />
                  <Stat icon={Scale} label="Goal weight" value={overview.client.goal_weight ? `${overview.client.goal_weight} ${overview.client.weight_unit || "lb"}` : "—"} />
                  <Stat icon={Syringe} label="Shots" value={overview.shots.length} />
                  <Stat icon={Utensils} label="Meals" value={overview.nutrition_logs.length} />
                </div>
              </div>

              <LogSection
                title="Recent shots"
                icon={Syringe}
                rows={overview.shots}
                testid="admin-client-shots"
                render={(r) => `${r.peptide_name} · ${r.dose_amount ?? r.dose ?? "?"} ${r.dose_unit || ""} · ${(r.taken_at || "").slice(0, 10)}`}
              />
              <LogSection
                title="Recent nutrition"
                icon={Utensils}
                rows={overview.nutrition_logs}
                testid="admin-client-nutrition"
                render={(r) => `${r.note || "meal"} — ${Math.round(r.calories || 0)} kcal · ${(r.logged_at || "").slice(0, 10)}`}
              />
              <LogSection
                title="Weight log"
                icon={Scale}
                rows={overview.weights}
                testid="admin-client-weights"
                render={(r) => `${r.weight} ${r.unit || "lb"} · ${(r.logged_at || "").slice(0, 10)}`}
              />
              <LogSection
                title="Supplements"
                icon={Pill}
                rows={overview.supplements}
                testid="admin-client-supplements"
                render={(r) => `${r.name}${r.time_hhmm ? " · " + r.time_hhmm : ""}`}
              />

              <CoachNotesPanel clientId={overview.client.id} clientHandle={overview.client.handle} admin />
              <ClientActivityFeed clientId={overview.client.id} />
            </>
          )}
        </div>
      </div>

      <ReassignDialog
        open={reassignOpen}
        client={client}
        allCoaches={allCoaches}
        onClose={() => setReassignOpen(false)}
        onReassigned={(payload) => {
          onReassigned(client.share_id, payload.new_coach_email, payload.new_coach_name || payload.new_coach_email);
          setReassignOpen(false);
        }}
      />
    </div>
  );
}

function ReassignDialog({ open, client, allCoaches, onClose, onReassigned }) {
  const [pickedEmail, setPickedEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) { setPickedEmail(""); setQ(""); }
  }, [open]);

  if (!open) return null;

  const currentEmail = (client.coach_email || "").toLowerCase();
  const options = (allCoaches || []).filter((c) => (c.email || "").toLowerCase() !== currentEmail);
  const filtered = q.trim()
    ? options.filter((c) => {
        const t = q.trim().toLowerCase();
        return (c.email || "").toLowerCase().includes(t) ||
               (c.name || "").toLowerCase().includes(t) ||
               (c.handle || "").toLowerCase().includes(t);
      })
    : options;

  const submit = async () => {
    if (!pickedEmail) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/coach-shares/${client.share_id}/reassign`, {
        new_coach_email: pickedEmail,
      });
      toast.success(`Reassigned to ${data.new_coach_name || data.new_coach_email}`);
      onReassigned({ new_coach_email: data.new_coach_email, new_coach_name: data.new_coach_name });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9995] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-reassign-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-reassign-dialog">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: AMBER }}>Reassign client</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">
              {client.name || `@${client.handle}`}
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Currently with <b className="text-teal-700">{client.coach_name || client.coach_email}</b>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Close" data-testid="admin-reassign-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {options.length === 0 ? (
            <div className="text-sm text-slate-500 italic border border-dashed border-slate-200 rounded-md p-6 text-center">
              No other coaches available to reassign to.
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter coaches…"
                  className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                  data-testid="admin-reassign-search"
                />
              </div>
              <ul className="space-y-1.5 max-h-64 overflow-auto" data-testid="admin-reassign-options">
                {filtered.map((c) => {
                  const isPicked = pickedEmail === c.email;
                  return (
                    <li key={c.user_id}>
                      <button
                        type="button"
                        onClick={() => setPickedEmail(c.email)}
                        className={`w-full text-left p-2.5 rounded-md flex items-center gap-3 border transition-colors ${
                          isPicked ? "border-amber-300 bg-amber-50" : "border-slate-100 hover:bg-slate-50 hover:border-slate-200"
                        }`}
                        data-testid={`admin-reassign-option-${c.user_id}`}
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                             style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}>
                          {(c.handle || c.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{c.name || `@${c.handle}`}</div>
                          <div className="text-[11px] text-slate-500 truncate font-mono-num">{c.email}</div>
                        </div>
                        <div className="text-[10px] text-slate-500 shrink-0">
                          <b className="text-slate-800 tabular-nums">{c.active_client_count}</b> clients
                        </div>
                        {isPicked && <Check className="w-4 h-4 text-amber-700 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="text-[11px] text-slate-500 italic">
                The client keeps all their data · shots, nutrition, weights, and notes. Only their assigned coach changes.
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100"
                  data-testid="admin-reassign-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={submit}
                  disabled={!pickedEmail || busy}
                  className="text-white"
                  style={{ background: AMBER }}
                  data-testid="admin-reassign-confirm">
            <UserCog className="w-3.5 h-3.5 mr-1" /> {busy ? "Reassigning…" : "Reassign"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: TEAL }} />
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function LogSection({ title, icon: Icon, rows, render, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: TEAL }} />
        <h3 className="font-display font-bold text-slate-900">{title}</h3>
        <span className="text-[10px] text-slate-400 ml-1 tabular-nums">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No entries yet.</div>
      ) : (
        <ul className="space-y-1 text-sm text-slate-700 max-h-64 overflow-auto">
          {rows.slice(0, 20).map((r) => (
            <li key={r.id} className="border-b border-slate-100 last:border-0 py-1.5">{render(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
